import React, { useState, useMemo } from 'react';
import './CamelotWheel.css';

// Camelot wheel key data with colors and harmonic relationships
const CAMELOT_KEYS = [
  { camelot: '1A', key: 'Ab minor', color: '#FF6B6B', position: 0 },
  { camelot: '1B', key: 'B major', color: '#FF8E8E', position: 1 },
  { camelot: '2A', key: 'Eb minor', color: '#FFA94D', position: 2 },
  { camelot: '2B', key: 'Gb major', color: '#FFB870', position: 3 },
  { camelot: '3A', key: 'Bb minor', color: '#FFD93D', position: 4 },
  { camelot: '3B', key: 'Db major', color: '#FFE566', position: 5 },
  { camelot: '4A', key: 'F minor', color: '#6BCB77', position: 6 },
  { camelot: '4B', key: 'Ab major', color: '#8ED99A', position: 7 },
  { camelot: '5A', key: 'C minor', color: '#4ECDC4', position: 8 },
  { camelot: '5B', key: 'Eb major', color: '#7EDAD3', position: 9 },
  { camelot: '6A', key: 'G minor', color: '#45B7D1', position: 10 },
  { camelot: '6B', key: 'Bb major', color: '#73C8DD', position: 11 },
  { camelot: '7A', key: 'D minor', color: '#6C5CE7', position: 12 },
  { camelot: '7B', key: 'F major', color: '#8E82EC', position: 13 },
  { camelot: '8A', key: 'A minor', color: '#A855F7', position: 14 },
  { camelot: '8B', key: 'C major', color: '#BE7FF8', position: 15 },
  { camelot: '9A', key: 'E minor', color: '#EC4899', position: 16 },
  { camelot: '9B', key: 'G major', color: '#F178B6', position: 17 },
  { camelot: '10A', key: 'B minor', color: '#F43F5E', position: 18 },
  { camelot: '10B', key: 'D major', color: '#F7707F', position: 19 },
  { camelot: '11A', key: 'F# minor', color: '#F97316', position: 20 },
  { camelot: '11B', key: 'A major', color: '#FB9A4B', position: 21 },
  { camelot: '12A', key: 'C# minor', color: '#EAB308', position: 22 },
  { camelot: '12B', key: 'E major', color: '#EFC440', position: 23 },
];

// Get compatible keys for harmonic mixing
const getCompatibleKeys = (selectedKey) => {
  if (!selectedKey) return [];
  
  const keyIndex = CAMELOT_KEYS.findIndex(k => k.camelot === selectedKey.camelot);
  if (keyIndex === -1) return [];
  
  const isMinor = selectedKey.camelot.endsWith('A');
  const keyNumber = parseInt(selectedKey.camelot);
  
  const compatible = [];
  
  // Same key (perfect match)
  compatible.push(selectedKey.camelot);
  
  // Relative major/minor (same number, different letter)
  const relativeKey = isMinor ? `${keyNumber}B` : `${keyNumber}A`;
  compatible.push(relativeKey);
  
  // Adjacent keys (+1 and -1)
  const prevNum = keyNumber === 1 ? 12 : keyNumber - 1;
  const nextNum = keyNumber === 12 ? 1 : keyNumber + 1;
  const letter = isMinor ? 'A' : 'B';
  compatible.push(`${prevNum}${letter}`);
  compatible.push(`${nextNum}${letter}`);
  
  // Energy boost (+1 to same type)
  compatible.push(`${nextNum}${letter}`);
  
  return [...new Set(compatible)];
};

const CamelotWheel = ({ onKeySelect, selectedKey, showLegend = true, size = 400 }) => {
  const [hoveredKey, setHoveredKey] = useState(null);
  
  const compatibleKeys = useMemo(() => {
    if (!selectedKey) return [];
    const selected = CAMELOT_KEYS.find(k => k.camelot === selectedKey);
    return selected ? getCompatibleKeys(selected) : [];
  }, [selectedKey]);

  // Calculate segment positions for the wheel
  const createWheelSegment = (key, index, isOuter) => {
    const totalSegments = 12;
    const segmentAngle = 360 / totalSegments;
    const keyNumber = parseInt(key.camelot);
    const startAngle = (keyNumber - 1) * segmentAngle - 90; // Start from top
    const endAngle = startAngle + segmentAngle;
    
    const outerRadius = size / 2 - 10;
    const innerRadius = isOuter ? size / 2 - 60 : size / 2 - 110;
    const midRadius = isOuter ? size / 2 - 35 : size / 2 - 85;
    
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    const midRad = ((startAngle + endAngle) / 2 * Math.PI) / 180;
    
    const x1 = size / 2 + (isOuter ? outerRadius : innerRadius + 50) * Math.cos(startRad);
    const y1 = size / 2 + (isOuter ? outerRadius : innerRadius + 50) * Math.sin(startRad);
    const x2 = size / 2 + (isOuter ? outerRadius : innerRadius + 50) * Math.cos(endRad);
    const y2 = size / 2 + (isOuter ? outerRadius : innerRadius + 50) * Math.sin(endRad);
    const x3 = size / 2 + innerRadius * Math.cos(endRad);
    const y3 = size / 2 + innerRadius * Math.sin(endRad);
    const x4 = size / 2 + innerRadius * Math.cos(startRad);
    const y4 = size / 2 + innerRadius * Math.sin(startRad);
    
    const textX = size / 2 + midRadius * Math.cos(midRad);
    const textY = size / 2 + midRadius * Math.sin(midRad);
    
    const isSelected = selectedKey === key.camelot;
    const isCompatible = compatibleKeys.includes(key.camelot);
    const isHovered = hoveredKey === key.camelot;
    
    const largeArcFlag = segmentAngle > 180 ? 1 : 0;
    
    const pathD = `
      M ${x1} ${y1}
      A ${isOuter ? outerRadius : innerRadius + 50} ${isOuter ? outerRadius : innerRadius + 50} 0 ${largeArcFlag} 1 ${x2} ${y2}
      L ${x3} ${y3}
      A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}
      Z
    `;
    
    return {
      pathD,
      textX,
      textY,
      isSelected,
      isCompatible,
      isHovered,
      key
    };
  };

  // Separate minor (A) and major (B) keys
  const minorKeys = CAMELOT_KEYS.filter(k => k.camelot.endsWith('A'));
  const majorKeys = CAMELOT_KEYS.filter(k => k.camelot.endsWith('B'));

  return (
    <div className="camelot-wheel-container">
      <div className="camelot-wheel-header">
        <h3>Harmonic Mixing Wheel</h3>
        <p className="camelot-subtitle">Click a key to see compatible transitions</p>
      </div>
      
      <svg 
        width={size} 
        height={size} 
        viewBox={`0 0 ${size} ${size}`}
        className="camelot-wheel-svg"
      >
        {/* Outer ring - Minor keys (A) */}
        {minorKeys.map((key, index) => {
          const segment = createWheelSegment(key, index, true);
          return (
            <g key={key.camelot} className="camelot-segment">
              <path
                d={segment.pathD}
                fill={key.color}
                stroke={segment.isSelected ? '#fff' : segment.isCompatible ? '#00ffff' : 'rgba(0,0,0,0.3)'}
                strokeWidth={segment.isSelected ? 3 : segment.isCompatible ? 2 : 1}
                opacity={selectedKey && !segment.isCompatible && !segment.isSelected ? 0.3 : 1}
                className={`camelot-segment-path ${segment.isSelected ? 'selected' : ''} ${segment.isCompatible ? 'compatible' : ''}`}
                onClick={() => onKeySelect && onKeySelect(key.camelot, key.key)}
                onMouseEnter={() => setHoveredKey(key.camelot)}
                onMouseLeave={() => setHoveredKey(null)}
                style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
              />
              <text
                x={segment.textX}
                y={segment.textY}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={segment.isSelected ? '#fff' : '#000'}
                fontSize="12"
                fontWeight={segment.isSelected ? 'bold' : 'normal'}
                pointerEvents="none"
                className="camelot-key-text"
              >
                {key.camelot}
              </text>
            </g>
          );
        })}
        
        {/* Inner ring - Major keys (B) */}
        {majorKeys.map((key, index) => {
          const segment = createWheelSegment(key, index, false);
          return (
            <g key={key.camelot} className="camelot-segment">
              <path
                d={segment.pathD}
                fill={key.color}
                stroke={segment.isSelected ? '#fff' : segment.isCompatible ? '#00ffff' : 'rgba(0,0,0,0.3)'}
                strokeWidth={segment.isSelected ? 3 : segment.isCompatible ? 2 : 1}
                opacity={selectedKey && !segment.isCompatible && !segment.isSelected ? 0.3 : 1}
                className={`camelot-segment-path ${segment.isSelected ? 'selected' : ''} ${segment.isCompatible ? 'compatible' : ''}`}
                onClick={() => onKeySelect && onKeySelect(key.camelot, key.key)}
                onMouseEnter={() => setHoveredKey(key.camelot)}
                onMouseLeave={() => setHoveredKey(null)}
                style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
              />
              <text
                x={segment.textX}
                y={segment.textY}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={segment.isSelected ? '#fff' : '#000'}
                fontSize="11"
                fontWeight={segment.isSelected ? 'bold' : 'normal'}
                pointerEvents="none"
                className="camelot-key-text"
              >
                {key.camelot}
              </text>
            </g>
          );
        })}
        
        {/* Center circle with info */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={size / 2 - 115}
          fill="rgba(0, 0, 0, 0.8)"
          stroke="rgba(255, 255, 255, 0.2)"
          strokeWidth="2"
        />
        <text
          x={size / 2}
          y={size / 2 - 15}
          textAnchor="middle"
          fill="#fff"
          fontSize="14"
          fontWeight="bold"
        >
          {selectedKey || 'Select Key'}
        </text>
        {selectedKey && (
          <text
            x={size / 2}
            y={size / 2 + 10}
            textAnchor="middle"
            fill="rgba(255,255,255,0.7)"
            fontSize="11"
          >
            {CAMELOT_KEYS.find(k => k.camelot === selectedKey)?.key || ''}
          </text>
        )}
      </svg>
      
      {/* Legend */}
      {showLegend && (
        <div className="camelot-legend">
          <div className="legend-item">
            <span className="legend-color outer"></span>
            <span>Minor Keys (A)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color inner"></span>
            <span>Major Keys (B)</span>
          </div>
          {selectedKey && (
            <div className="legend-item compatible-indicator">
              <span className="legend-color compatible-dot"></span>
              <span>Compatible Keys</span>
            </div>
          )}
        </div>
      )}
      
      {/* Selected Key Info */}
      {selectedKey && (
        <div className="camelot-selected-info">
          <h4>Compatible Keys for {selectedKey}</h4>
          <div className="compatible-keys-list">
            {compatibleKeys.map(ck => {
              const keyData = CAMELOT_KEYS.find(k => k.camelot === ck);
              return (
                <div 
                  key={ck} 
                  className="compatible-key-badge"
                  style={{ backgroundColor: keyData?.color || '#666' }}
                  onClick={() => onKeySelect && onKeySelect(ck, keyData?.key)}
                >
                  <span className="badge-camelot">{ck}</span>
                  <span className="badge-key">{keyData?.key}</span>
                </div>
              );
            })}
          </div>
          <p className="mixing-tip">
            Tip: Mix between these keys for smooth, harmonic transitions
          </p>
        </div>
      )}
    </div>
  );
};

export default CamelotWheel;
